import React, { useEffect, useState } from 'react';
import './MasterDeadlineDocket.scss';
import filterFactory from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-filter';
import BootstrapTable from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import paginationFactory from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-paginator';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../../store/selectors';
import { TableFilter } from '../../../../components/Table/models/table-filter';
import Helpers from '../../../../utils/helper';
import { GetCurrentEventsOfAllOpportunities } from '../../../../slices/marketplace/opportunity/thunks/get-current-envents-of-all-opp.thunk';
import { CurrentEvent } from '../../../../slices/marketplace/opportunity/models/current-event';
import moment from 'moment';
import {
    LocationType,
    LocationTypeOptions,
} from '../../../../slices/marketplace/timeline/models/enums/location-type.enum';
import { stateToCodeMapper } from '../../../../utils/locationsHelper';
import { ShowFirstThenFullTextOnHoverLabel } from '../../../../components/ShowFirstThenFullTextOnHoverLabel/ShowFirstThenFullTextOnHoverLabel';
import { useNavigate } from 'react-router-dom';
import { BuyerMarketplaceTab } from '../../../../slices/marketplace/timeline/models/enums/buyer-marketplace-tab.enum';
import { SaveSelectedOpportunity } from '../../../../slices/marketplace/buyer/thunks/save-selected-opportunity.thunk';
import { setSelectedOpportunity } from '../../../../slices/marketplace/opportunity/opportunity.slice';
import { NotificationFlag } from '../../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../../models/NotificationFlags/NotificationFlagsCategories';

interface MasterDeadlineDocketProps {
    setActiveTab?: (tab: number) => void;
    selectedOpportunityId: number;
}

const MasterDeadlineDocket = (props: MasterDeadlineDocketProps) => {
    const dispatch = useDispatch() as any;
    const navigate = useNavigate();
    const currentEventsOfAllOpportunities = useStateSelector(
        (state) => state.opportunities.currentEventsOfAllOpportunities
    );
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(50);
    const [events, setEvents] = useState<CurrentEvent[]>([]);

    const defaultSorted: [{ dataField: any; order: any }] = [
        {
            dataField: 'deadline',
            order: 'asc',
        },
    ];

    const [currentSort, setCurrentSort] = useState<TableFilter>(
        new TableFilter({ filters: [], logic: '' }, page, sizePerPage, {
            dir: 'asc',
            field: 'deadline',
        })
    );

    const handleTableChange = (_type: any, newState: any) => {
        const { page, sizePerPage, sortField, sortOrder } = newState;

        const updatedPostObj = new TableFilter(
            { filters: [], logic: '' },
            page,
            sizePerPage,
            { dir: sortOrder, field: sortField }
        );
        setCurrentSort(updatedPostObj);
    };

    const fetchCurrentEvents = async () => {
        await dispatch(GetCurrentEventsOfAllOpportunities(currentSort));
    };

    useEffect(() => {
        fetchCurrentEvents()
            .then()
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        fetchCurrentEvents();
    }, [currentSort]);

    useEffect(() => {
        setEvents(currentEventsOfAllOpportunities.events);
        setPage(currentEventsOfAllOpportunities.pageNumber);
        setTotalSize(currentEventsOfAllOpportunities.totalItems);
        setSizePerPage(currentEventsOfAllOpportunities.pageSize);
    }, [fetchCurrentEvents]);

    const selectOppThenNavigate = (opportunityId: number, eventId: string) => {
        if (props.selectedOpportunityId !== opportunityId) {
            dispatch(SaveSelectedOpportunity(opportunityId));
            dispatch(setSelectedOpportunity(opportunityId));
        }

        navigate(`/opportunities?opportunityId=${opportunityId}&tab=${2}`, {
            state: {
                selectedEventId: eventId,
            },
        });
    };

    const templatesColumns = [
        {
            dataField: 'deadline',
            text: 'Deadline',
            headerClasses: 'deadline-column-header',
            sort: true,
            formatter: (cellContent: number) => {
                return <div>{moment(cellContent).format('M/D/YYYY')}</div>;
            },
        },
        {
            dataField: 'opportunityId',
            text: 'OppID',
            headerClasses: 'oppid-column-header',
            sort: true,
            formatter: (cellContent: string) => {
                return <div>{cellContent}</div>;
            },
        },
        {
            dataField: 'practiceNames',
            text: 'Practice Name',
            headerClasses: 'practice-name-column-header',
            formatter: (cellContent: string[], row: CurrentEvent) => {
                return !row.isIdNdaSigned ? (
                    'NA'
                ) : (
                    <ShowFirstThenFullTextOnHoverLabel
                        items={cellContent}
                        tooltipKey={`${row.opportunityId}-${row.eventName}`}
                    />
                );
            },
        },
        {
            dataField: 'practiceLocations',
            text: 'Practice Location',
            headerClasses: 'practice-locations-column-header',
            formatter: (cellContent: string[], row: CurrentEvent) => {
                const codesOfStates = cellContent.map((location) =>
                    stateToCodeMapper(location)
                );
                return (
                    <ShowFirstThenFullTextOnHoverLabel
                        items={codesOfStates}
                        tooltipKey={`${row.opportunityId}-${row.eventName}`}
                    />
                );
            },
        },
        {
            dataField: 'locationTypes',
            text: 'Location Type',
            headerClasses: 'location-types-column-header',
            formatter: (cellContent: LocationType[], row: CurrentEvent) => {
                return !cellContent || cellContent.length === 0 ? (
                    'NA'
                ) : (
                    <ShowFirstThenFullTextOnHoverLabel
                        items={cellContent.map(
                            (type) => LocationTypeOptions[type]
                        )}
                        tooltipKey={`${row.opportunityId}-${row.eventName}`}
                    />
                );
            },
        },
        {
            dataField: 'fteDvms',
            text: 'FTE DVMs',
            headerClasses: 'ftedvms-column-header',
            sort: true,
            formatter: (cellContent: string) => {
                return <div>{cellContent}</div>;
            },
        },
        {
            dataField: 'stage',
            text: 'Stage',
            headerClasses: 'stage-column-header',
            sort: true,
            formatter: (cellContent: string) => {
                return <div>{cellContent}</div>;
            },
        },
        {
            dataField: 'eventName',
            text: 'Step',
            headerClasses: 'step-column-header',
            sort: true,
            formatter: (cellContent: string, row: CurrentEvent) => {
                return (
                    <div
                        className="notification-flag-wrapper link-text"
                        onClick={() => {
                            props.setActiveTab(
                                BuyerMarketplaceTab.Opportunities
                            );

                            selectOppThenNavigate(
                                row.opportunityId,
                                row.event.id
                            );
                        }}>
                        {cellContent}

                        <NotificationFlag
                            eventCode={`${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.MasterDeadlineDocketCode}.${row.opportunityId}.${row.event.id}`}/>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="master-deadline-docket">
            <div className="body_inner_content">
                <div className="obpbq_row valuation_section">
                    <div className="sampledetail">
                        <div
                            role="tabpanel"
                            id="panel-0"
                            aria-labelledby="tab-0"
                            aria-hidden="false">
                            <div className="card-body">
                                {isLoading ? (
                                    Helpers.renderTableLoader()
                                ) : (
                                    <BootstrapTable
                                        striped
                                        bordered
                                        bootstrap4
                                        remote
                                        keyField="id"
                                        data={events}
                                        columns={templatesColumns}
                                        defaultSorted={defaultSorted}
                                        filter={filterFactory()}
                                        pagination={paginationFactory({
                                            page,
                                            sizePerPage,
                                            totalSize,
                                            tooltipTargetId:
                                                'current-events-tooltip',
                                        })}
                                        onTableChange={handleTableChange}
                                        noDataIndication="No Events available"
                                        rowStyle={{ height: '44px' }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MasterDeadlineDocket;
